import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLogin, useAdminRecoverySend, useAdminRecoveryVerify } from "../hooks/useApi";
import Button from "../components/Button";
import OtpAuth from "../components/OtpAuth";

type Tab = "password" | "otp";

const DIGIT_MAP = "۰۱۲۳۴۵۶۷۸۹٠١٢٣٤٥٦٧٨٩";
const LATIN = "01234567890123456789";
function normalizeDigits(v: string): string {
  let out = "";
  for (const ch of v) {
    const idx = DIGIT_MAP.indexOf(ch);
    out += idx !== -1 ? LATIN[idx % 10] : ch;
  }
  return out;
}

export default function Login() {
  const [searchParams] = useSearchParams();
  const initialPhone = searchParams.get("phone") || undefined;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [tab, setTab] = useState<Tab>("otp");
  const login = useLogin();

  // ── فراموشی رمز عبور (recovery) state — only inside password tab ──
  const [isRecovery, setIsRecovery] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState<"identifier" | "code">("identifier");
  const [identifier, setIdentifier] = useState("");
  const [identifierError, setIdentifierError] = useState("");
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [recoveryChannel, setRecoveryChannel] = useState<"sms" | "email" | null>(null);
  const [normalizedIdentifier, setNormalizedIdentifier] = useState("");
  const timerRef = useRef<number | null>(null);
  const recoverySend = useAdminRecoverySend();
  const recoveryVerify = useAdminRecoveryVerify();

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (cooldown <= 0 && timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [cooldown]);

  // Reset recovery when switching tabs
  useEffect(() => {
    if (tab !== "password" && isRecovery) {
      setIsRecovery(false);
      setRecoveryStep("identifier");
      setIdentifier("");
      setIdentifierError("");
      setCode("");
      setCooldown(0);
      setRecoveryChannel(null);
      setNormalizedIdentifier("");
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [tab, isRecovery]);

  const startCooldown = (sec: number) => {
    setCooldown(sec);
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000) as unknown as number;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login.mutate({ email, password });
  };

  const validateIdentifier = (raw: string): string | null => {
    const t = raw.trim();
    if (!t) return "ایمیل را وارد کنید";
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(t)) return "فرمت ایمیل نامعتبر است";
    return null;
  };

  const handleRecoverySend = (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateIdentifier(identifier);
    if (err) {
      setIdentifierError(err);
      return;
    }
    setIdentifierError("");
    const raw = identifier.trim();
    recoverySend.mutate(
      { identifier: raw },
      {
        onSuccess: (data: Record<string, unknown>) => {
          const channel = (data.channel as string) || (raw.includes("@") ? "email" : "sms");
          setRecoveryChannel(channel as "sms" | "email");
          setNormalizedIdentifier((data.identifier as string) || raw);
          setRecoveryStep("code");
          setCode("");
          const expires = (data.expires_in as number) || 120;
          startCooldown(expires);
        },
      }
    );
  };

  const handleRecoveryVerify = (e: React.FormEvent) => {
    e.preventDefault();
    const codeDigits = normalizeDigits(code).replace(/[^0-9]/g, "").slice(0, 5);
    if (codeDigits.length !== 5) return;
    recoveryVerify.mutate({ identifier: normalizedIdentifier || identifier.trim(), code: codeDigits });
  };

  const handleRecoveryResend = () => {
    if (cooldown > 0) return;
    const raw = normalizedIdentifier || identifier.trim();
    recoverySend.mutate(
      { identifier: raw },
      {
        onSuccess: (data: Record<string, unknown>) => {
          const expires = (data.expires_in as number) || 120;
          startCooldown(expires);
        },
      }
    );
  };

  const handleToggleRecovery = () => {
    if (isRecovery) {
      setIsRecovery(false);
      setRecoveryStep("identifier");
      setIdentifier("");
      setIdentifierError("");
      setCode("");
      setCooldown(0);
      setRecoveryChannel(null);
      setNormalizedIdentifier("");
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    } else {
      setIsRecovery(true);
      setRecoveryStep("identifier");
      setIdentifierError("");
    }
  };

  const handleEditIdentifier = () => {
    setRecoveryStep("identifier");
    setCode("");
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setCooldown(0);
  };

  const handleIdentifierChange = (v: string) => {
    setIdentifier(v);
    if (identifierError) setIdentifierError("");
  };

  const handleCodeChange = (v: string) => {
    const digits = normalizeDigits(v).replace(/[^0-9]/g, "").slice(0, 5);
    setCode(digits);
  };

  return (
    <div className="card p-8">
      <div className="text-center mb-6">
        <img src="/logo-reverse-filled.svg" alt="ASHA-AI" className="w-12 h-12 mx-auto mb-4" />
        <h1 className="text-h1 text-text-main mb-1">ورود</h1>
        <p className="text-body text-text-secondary">به پنل مدیریت AI Gateway خوش آمدید</p>
      </div>

      <div className="flex p-1 mb-6 bg-surface-base rounded-btn border border-line">
        <button
          type="button"
          onClick={() => setTab("password")}
          className={`flex-1 py-2 px-3 rounded-btn text-small font-medium transition-all ${
            tab === "password" ? "bg-surface shadow-sm text-text-main border border-line" : "text-text-tertiary hover:text-text-secondary"
          }`}
        >
          ایمیل و رمز عبور
        </button>
        <button
          type="button"
          onClick={() => setTab("otp")}
          className={`flex-1 py-2 px-3 rounded-btn text-small font-medium transition-all ${
            tab === "otp" ? "bg-surface shadow-sm text-text-main border border-line" : "text-text-tertiary hover:text-text-secondary"
          }`}
        >
          ورود با رمز یکبار مصرف
        </button>
      </div>

      {tab === "password" ? (
        isRecovery ? (
          <div className="space-y-4">
            {recoveryStep === "identifier" ? (
              <form onSubmit={handleRecoverySend} className="space-y-4">
                <div>
                  <label className="block text-small font-medium text-text-secondary mb-1.5">
                    ایمیل
                  </label>
                  <input
                    type="email"
                    value={identifier}
                    onChange={(e) => handleIdentifierChange(e.target.value)}
                    className={`w-full h-10 px-3.5 rounded-btn border text-body text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all ${
                      identifierError ? "border-status-error-text" : "border-line"
                    }`}
                    placeholder="example@email.com"
                    dir="ltr"
                    autoComplete="email"
                    autoFocus
                    required
                  />
                  {identifierError ? (
                    <p className="text-xs text-status-error-text mt-1.5">{identifierError}</p>
                  ) : (
                    <p className="text-xs text-text-tertiary mt-1.5">
                      کد تایید ۵ رقمی به ایمیل شما ارسال خواهد شد
                    </p>
                  )}
                </div>

                <Button type="submit" className="w-full !py-2.5" loading={recoverySend.isPending}>
                  ارسال کد تایید
                </Button>

                <button
                  type="button"
                  onClick={handleToggleRecovery}
                  className="w-full text-center text-small text-text-tertiary hover:text-text-secondary transition-colors"
                >
                  بازگشت به ورود با رمز عبور
                </button>
              </form>
            ) : (
              <form onSubmit={handleRecoveryVerify} className="space-y-4">
                <div className="rounded-lg bg-primary-light/30 border border-primary/20 px-3.5 py-2.5 flex items-center justify-between gap-2">
                  <span className="text-small text-text-secondary">
                    کد به ایمیل{" "}
                    <span dir="ltr" className="font-medium text-text-main">
                      {normalizedIdentifier || identifier}
                    </span>{" "}
                    ارسال شد
                  </span>
                  <button
                    type="button"
                    onClick={handleEditIdentifier}
                    className="text-xs text-primary hover:underline font-medium shrink-0"
                  >
                    ویرایش
                  </button>
                </div>

                <div>
                  <label className="block text-small font-medium text-text-secondary mb-1.5">کد تایید</label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => handleCodeChange(e.target.value)}
                    className="w-full h-11 px-3.5 rounded-btn border border-line text-center tracking-[0.45em] text-lg font-medium text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                    placeholder="─ ─ ─ ─ ─"
                    dir="ltr"
                    inputMode="numeric"
                    maxLength={5}
                    autoComplete="one-time-code"
                    autoFocus
                  />
                  <div className="flex items-center justify-between mt-2">
                    {cooldown > 0 ? (
                      <span className="text-xs text-text-tertiary">ارسال مجدد تا {cooldown} ثانیه دیگر</span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleRecoveryResend}
                        disabled={recoverySend.isPending}
                        className="text-xs text-primary hover:underline font-medium disabled:opacity-50"
                      >
                        ارسال مجدد کد
                      </button>
                    )}
                    <span className="text-xs text-text-tertiary">{code.length}/5</span>
                  </div>
                </div>

                <Button type="submit" className="w-full !py-2.5" loading={recoveryVerify.isPending} disabled={code.length !== 5}>
                  تایید و ورود
                </Button>

                <button
                  type="button"
                  onClick={handleEditIdentifier}
                  className="w-full text-center text-small text-text-tertiary hover:text-text-secondary transition-colors"
                >
                  بازگشت و ویرایش ایمیل
                </button>
                <button
                  type="button"
                  onClick={handleToggleRecovery}
                  className="w-full text-center text-small text-text-tertiary hover:text-text-secondary transition-colors"
                >
                  بازگشت به ورود با رمز عبور
                </button>
              </form>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-small font-medium text-text-secondary mb-1.5">ایمیل</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-10 px-3.5 rounded-btn border border-line text-body text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                placeholder="example@email.com"
                required
                dir="ltr"
              />
            </div>

            <div>
              <label className="block text-small font-medium text-text-secondary mb-1.5">رمز عبور</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-10 pl-9 pr-3.5 rounded-btn border border-line text-body text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  placeholder="••••••••"
                  required
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? "مخفی کردن رمز عبور" : "نمایش رمز عبور"}
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              <div className="flex justify-end mt-1.5">
                <button
                  type="button"
                  onClick={handleToggleRecovery}
                  className="text-xs text-primary hover:underline font-medium"
                >
                  فراموشی رمز عبور
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full !py-2.5" loading={login.isPending}>
              ورود
            </Button>
          </form>
        )
      ) : (
        <OtpAuth submitLabel="تایید و ورود" mode="login" initialPhone={initialPhone} />
      )}

      <p className="text-center text-small text-text-tertiary mt-6">
        حساب کاربری ندارید؟{" "}
        <Link to="/signup" className="text-primary hover:underline font-medium">
          ثبت نام
        </Link>
      </p>
    </div>
  );
}
